import React, { useMemo, useState } from "react";
import {
  Box,
  Container,
  Grid,
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Breadcrumbs,
  Link as MLink,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { red } from "@mui/material/colors";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import BookmarkRoundedIcon from "@mui/icons-material/BookmarkRounded";
import BookmarkAddRoundedIcon from "@mui/icons-material/BookmarkAddRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import { Link as RouterLink } from "react-router-dom";

// ====== Use your real data (no TypeScript) ======
// Create ../data/courseData.js with exactly the export you provided:
// export const courseData = [ ... ]
import { courseData } from "./data/courseData";
import Header from "./header";
import Footer from "./footer";
import Seo from "./components/Seo";
import seoRoutes from "../seo/seoRoutes";

const ORANGE_ACCENT = "#F59E0B"; // nav color tweak accent (amber vibe)

function slugify(s) {
  return String(s)
    .toLowerCase()
    .trim()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function getStreamColor(stream) {
  if (!stream) return red[600];
  const map = {
    Arts: "#be185d",
    Science: "#2563eb",
    Commerce: "#b45309",
  };
  return map[stream] || red[600];
}

export default function CoursesListPage() {
  const COURSES = courseData; // <- from your file
const seo = seoRoutes["/syllabus"];
  // Saved bookmarks
  const [saved, setSaved] = useState(new Set());
  const toggleSaved = (idx) =>
    setSaved((prev) => {
      const next = new Set(prev);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });

  // Filters
  const [query, setQuery] = useState("");
  const [stream, setStream] = useState("All");
  const [sort, setSort] = useState("az");

  // Build streams list dynamically from data
  const STREAMS = useMemo(() => {
    const unique = Array.from(
      new Set((COURSES || []).map((c) => c.stream).filter(Boolean))
    );
    return ["All", ...unique];
  }, [COURSES]);

  const filteredCourses = useMemo(() => {
    let list = [...(COURSES || [])];
    if (stream !== "All") list = list.filter((c) => c.stream === stream);
    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter(
        (c) =>
          c.courseName.toLowerCase().includes(q) ||
          (c.stream && c.stream.toLowerCase().includes(q)) ||
          (c.courseDesc || []).some((d) => d.toLowerCase().includes(q))
      );
    }
    switch (sort) {
      case "za":
        list.sort((a, b) => b.courseName.localeCompare(a.courseName));
        break;
      case "stream":
        list.sort(
          (a, b) =>
            (a.stream || "").localeCompare(b.stream || "") ||
            a.courseName.localeCompare(b.courseName)
        );
        break;
      default:
        list.sort((a, b) => a.courseName.localeCompare(b.courseName)); // az
    }
    return list;
  }, [COURSES, query, stream, sort]);

  return (
    <>
    <Seo title={seo.title} description={seo.description} path="/syllabus" image={seo.image} />
    <Box sx={{ bgcolor: "background.default", minHeight: "100vh" }}>
      <Header />

      {/* Header */}
      <Box
        sx={{
          borderBottom: (t) => `1px solid ${t.palette.divider}`,
          background: (t) =>
            `linear-gradient(135deg, ${alpha(
              t.palette.error.main,
              0.06
            )}, ${alpha(ORANGE_ACCENT, 0.08)})`,
        }}
      >
        <Container sx={{ py: { xs: 1.5, md: 2 } }}>
          <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: 13 }}>
            <MLink
              component={RouterLink}
              underline="hover"
              color="inherit"
              to="/"
            >
              Home
            </MLink>
            <Typography color="text.primary" fontWeight={700}>
              Courses
            </Typography>
          </Breadcrumbs>
          <Typography
            component="h1"
            sx={{
              mt: 0.75,
              fontWeight: 900,
              fontSize: { xs: 24, sm: 30, md: 34 },
              lineHeight: 1.15,
              background: `#000`,
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              color: "transparent",
            }}
          >
            Explore Courses
          </Typography>
        </Container>
      </Box>

      <Container sx={{ py: { xs: 2, md: 3 } }}>
        {/* Filters Row */}
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              label="Search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={6} sm={4} md={3}>
            <FormControl fullWidth>
              <InputLabel>Stream</InputLabel>
              <Select
                label="Stream"
                value={stream}
                onChange={(e) => setStream(e.target.value)}
              >
                {STREAMS.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={4} md={2.5}>
            <FormControl fullWidth>
              <InputLabel>Sort</InputLabel>
              <Select
                label="Sort"
                value={sort}
                onChange={(e) => setSort(e.target.value)}
              >
                <MenuItem value="az">A–Z</MenuItem>
                <MenuItem value="za">Z–A</MenuItem>
                <MenuItem value="stream">By Stream</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md="auto">
            <Button
              onClick={() => {
                setQuery("");
                setStream("All");
                setSort("az");
              }}
              variant="outlined"
            >
              Reset
            </Button>
          </Grid>
        </Grid>

        {/* Result count */}
        <Typography sx={{ mt: 2 }} color="text.secondary" variant="body2">
          {filteredCourses.length} courses found
        </Typography>

        {/* Courses grid */}
        <Grid
          container
          spacing={{ xs: 1.5, sm: 2, md: 2.5 }}
          columns={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}
          sx={{ mt: 0.5 }}
        >
          {filteredCourses.map((item, idx) => {
            const titleId = `course-title-${idx}`;
            const streamColor = getStreamColor(item.stream || item.tag);
            return (
              <Grid key={idx} item xs={12} sm={6} md={4} lg={3}>
                <Card
                  role="article"
                  aria-labelledby={titleId}
                  sx={{
                    position: "relative",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: { xs: 2, md: 3 },
                    bgcolor: "background.paper",
                    overflow: "hidden",
                    boxShadow: "0 8px 24px rgba(0,0,0,.08)",
                    // gradient ring
                    "&:before": {
                      content: '""',
                      position: "absolute",
                      inset: 0,
                      borderRadius: { xs: 8, md: 12 },
                      padding: "1px",
                      background: `linear-gradient(135deg, ${alpha(
                        red[600],
                        0.6
                      )}, ${alpha(ORANGE_ACCENT, 0.7)})`,
                      WebkitMask:
                        "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
                      WebkitMaskComposite: "xor",
                      maskComposite: "exclude",
                      pointerEvents: "none",
                    },
                    transition: "transform .25s ease, box-shadow .25s ease",
                    "@media (hover: hover) and (pointer: fine)": {
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: "0 16px 36px rgba(0,0,0,.14)",
                      },
                    },
                  }}
                >
                  <CardActionArea
                    component={RouterLink}
                    to={`/courses/${slugify(item.courseName)}`}
                    sx={{ alignItems: "stretch" }}
                  >
                    <Box sx={{ position: "relative" }}>
                      <CardMedia
                        component="img"
                        src={item.image}
                        alt={`${item.courseName} image`}
                        loading="lazy"
                        sizes="(max-width:600px) 100vw, (max-width:1200px) 50vw, 25vw"
                        sx={{
                          aspectRatio: "16/10",
                          width: "100%",
                          objectFit: "cover",
                          transform: "scale(1)",
                          transition: "transform .6s cubic-bezier(.2,.6,.2,1)",
                          "@media (hover: hover) and (pointer: fine)": {
                            ".MuiCard-root:hover &": {
                              transform: "scale(1.06)",
                            },
                          },
                        }}
                      />

                      {(item.stream || item.tag) && (
                        <Chip
                          size="small"
                          icon={
                            <StarRoundedIcon
                              sx={{ fontSize: 16, color: streamColor }}
                            />
                          }
                          label={item.stream || item.tag}
                          sx={{
                            position: "absolute",
                            top: 10,
                            left: 10,
                            bgcolor: alpha("#fff", 0.92),
                            backdropFilter: "saturate(140%) blur(4px)",
                            fontWeight: 800,
                            color: streamColor,
                            border: `1px solid ${alpha(streamColor, 0.4)}`,
                            maxWidth: { xs: "88%", sm: "75%" },
                          }}
                        />
                      )}

                      <Box
                        sx={{
                          position: "absolute",
                          left: 0,
                          right: 0,
                          bottom: 0,
                          height: "36%",
                          background:
                            "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,.45) 100%)",
                          pointerEvents: "none",
                        }}
                      />
                    </Box>
                  </CardActionArea>

                  <CardContent
                    sx={{
                      p: { xs: 1.25, sm: 1.5, md: 1.75 },
                      pt: { xs: 1, sm: 1.25 },
                      display: "flex",
                      flexDirection: "column",
                      flexGrow: 1,
                      gap: { xs: 0.75, sm: 1 },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography
                        id={titleId}
                        component={RouterLink}
                        to={`/courses/${slugify(item.courseName)}`}
                        style={{ textDecoration: "none" }}
                        sx={{
                          color: "text.primary",
                          fontWeight: 900,
                          letterSpacing: 0.2,
                          fontSize: { xs: 15, sm: 16, md: 16.5 },
                          lineHeight: 1.25,
                          background: `#000`,
                          WebkitBackgroundClip: "text",
                          backgroundClip: "text",
                          color: "transparent",
                          transition: "opacity .2s ease",
                          "@media (hover: hover) and (pointer: fine)": {
                            "&:hover": { opacity: 0.85 },
                          },
                        }}
                      >
                        {item.courseName}
                      </Typography>

                      <Tooltip
                        title={saved.has(idx) ? "Remove bookmark" : "Bookmark"}
                      >
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            toggleSaved(idx);
                          }}
                          sx={{
                            ml: "auto",
                            borderRadius: 1.5,
                            p: { xs: 0.75, sm: 0.5 },
                            minWidth: 40,
                            minHeight: 40,
                            bgcolor: "transparent",
                            color: saved.has(idx) ? red[600] : "text.secondary",
                            transition: "all .2s ease",
                            "@media (hover: hover) and (pointer: fine)": {
                              "&:hover": {
                                bgcolor: alpha(ORANGE_ACCENT, 0.12),
                                color: ORANGE_ACCENT,
                              },
                            },
                          }}
                          aria-label={`Bookmark ${item.courseName}`}
                        >
                          {saved.has(idx) ? (
                            <BookmarkRoundedIcon
                              sx={{ fontSize: { xs: 20, sm: 18 } }}
                            />
                          ) : (
                            <BookmarkAddRoundedIcon
                              sx={{ fontSize: { xs: 20, sm: 18 } }}
                            />
                          )}
                        </IconButton>
                      </Tooltip>
                    </Box>

                    <Box component="ul" sx={{ pl: 2, m: 0 }}>
                      {(item.courseDesc || []).map((d, i) => (
                        <Typography
                          key={i}
                          component="li"
                          sx={{
                            fontSize: { xs: 12.5, sm: 13 },
                            color: "text.secondary",
                            lineHeight: 1.55,
                            mb: 0.5,
                            display: "-webkit-box",
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            WebkitLineClamp: { xs: 2, sm: 3 },
                          }}
                        >
                          {d}
                        </Typography>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Container>
      <Footer />
    </Box>
    </>
  );
}
